import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Loader2,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Checkbox } from "@pgs-core/ui/components/checkbox";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Textarea } from "@pgs-core/ui/components/textarea";
import { Alert, AlertDescription } from "@pgs-core/ui/components/alert";
import { ErrorAlert } from "@pgs-core/ui/components/error-alert";
import { LoadingSpinner } from "@pgs-core/ui/components/loading-spinner";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { fetchForm, updateForm, type Form, type FormField, type FormFieldType } from "../lib/forms";
import type { ClientOutletContext } from "../lib/outlet-context";

// Palette of field types the builder offers, in display order.
const FIELD_TYPES: { type: FormFieldType; label: string }[] = [
  { type: "text", label: "Short text" },
  { type: "textarea", label: "Long text" },
  { type: "email", label: "Email" },
  { type: "number", label: "Number" },
  { type: "select", label: "Dropdown" },
  { type: "radio", label: "Multiple choice" },
  { type: "checkbox", label: "Checkboxes" },
  { type: "date", label: "Date" },
  { type: "time", label: "Time" },
  { type: "file", label: "File upload" },
  { type: "consent", label: "Consent" },
];
const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  FIELD_TYPES.map(({ type, label }) => [type, label])
);

const OPTION_TYPES = new Set<FormFieldType>(["select", "radio", "checkbox"]);
const TEXTUAL_TYPES = new Set<FormFieldType>(["text", "textarea", "email", "number"]);
const FILE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "pdf", "doc", "docx", "txt", "csv"];

/** A field in the builder: the saved shape plus a stable client-side key. */
type BuilderField = FormField & { _key: string };
type BuilderForm = Omit<Form, "fields"> & { fields: BuilderField[] };

function withKeys(form: Form): BuilderForm {
  return {
    ...form,
    fields: form.fields.map((field) => ({ ...field, _key: field.id ?? crypto.randomUUID() })),
  };
}

function newField(type: FormFieldType): BuilderField {
  return {
    _key: crypto.randomUUID(),
    type,
    label: type === "consent" ? "I agree to my data being stored to handle this enquiry" : `${TYPE_LABELS[type]} field`,
    required: type === "consent",
    placeholder: "",
    defaultValue: "",
    helpText: "",
    cssClass: "",
    ...(OPTION_TYPES.has(type) ? { options: ["Option 1", "Option 2"] } : {}),
    ...(type === "number" ? { min: null, max: null } : {}),
    ...(type === "text" || type === "textarea" ? { maxLength: null } : {}),
    ...(type === "file" ? { accept: [], maxSizeMb: null } : {}),
  };
}

/** Small labelled input used throughout the field editor grid. */
function ConfigInput({ label, id, ...props }: { label: React.ReactNode; id: string; [key: string]: unknown }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} {...props} />
    </div>
  );
}

function numberOrNull(value: string): number | null {
  return value === "" ? null : Number(value);
}

export function FormBuilderPage() {
  const { api, refreshStatus, setBreadcrumbs } = useOutletContext<ClientOutletContext>();
  const { id = "" } = useParams();

  const [form, setForm] = useState<BuilderForm | null>(null);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!api) return;
    fetchForm(api, id)
      .then((data) => setForm(withKeys(data)))
      .catch((err: Error) => setError(err.message));
  }, [api, id]);

  // Drill-in trail in the portal header: Forms › <form name> (spec §2.7/§4.8).
  useEffect(() => {
    if (!setBreadcrumbs) return undefined;
    setBreadcrumbs(form ? [
      { label: "Forms", href: "/forms/build" },
      { label: form.name || "Form" },
    ] : null);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs, form?.name]);

  const touch = (updater: (prev: BuilderForm) => BuilderForm) => {
    setForm((prev) => (prev ? updater(prev) : prev));
    setDirty(true);
  };

  const setField = (key: string, fieldPatch: Partial<FormField>) => {
    touch((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => (field._key === key ? { ...field, ...fieldPatch } : field)),
    }));
  };

  const setSetting = (settingsPatch: Partial<BuilderForm["settings"]>) => {
    touch((prev) => ({ ...prev, settings: { ...prev.settings, ...settingsPatch } }));
  };

  const addField = (type: FormFieldType) => {
    const field = newField(type);
    touch((prev) => ({ ...prev, fields: [...prev.fields, field] }));
    setExpandedKey(field._key);
  };

  const removeField = (key: string) => {
    touch((prev) => ({ ...prev, fields: prev.fields.filter((field) => field._key !== key) }));
    if (expandedKey === key) setExpandedKey(null);
  };

  const moveField = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || !form || target >= form.fields.length) return;
    touch((prev) => {
      const fields = [...prev.fields];
      const [moved] = fields.splice(index, 1);
      fields.splice(target, 0, moved);
      return { ...prev, fields };
    });
  };

  // Native HTML5 drag-and-drop: the list reorders live as the dragged row
  // passes over its siblings; the arrow buttons stay as the keyboard path.
  const onDragOverRow = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    moveField(dragIndex, index - dragIndex);
    setDragIndex(index);
  };

  const save = async () => {
    if (!api || !form) return;
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        name: form.name,
        fields: form.fields.map(({ _key, ...field }) => field),
        settings: form.settings,
      };
      const saved = await updateForm(api, id, payload);
      setForm(withKeys(saved));
      setDirty(false);
      refreshStatus?.();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const retention = form?.settings?.retentionDays;
  const retentionValue = useMemo(() => (retention === null || retention === undefined ? "" : String(retention)), [retention]);

  if (error) {
    return (
      <div>
        <PageHeader title="Form" />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!form) {
    return <LoadingSpinner className="py-20" />;
  }

  return (
    <div>
      <PageHeader
        title={form.name}
        description="Build the fields your visitors fill in, then add this form to any page with the Form block in the page editor."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/forms/build">
                <ArrowLeft className="h-4 w-4" />
                Back to forms
              </Link>
            </Button>
            <Button onClick={save} size="sm" disabled={saving || !dirty}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        }
      />

      <ErrorAlert error={saveError} />

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Form name</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={form.name}
              maxLength={100}
              aria-label="Form name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => touch((prev) => ({ ...prev, name: e.target.value }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Fields</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.fields.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No fields yet — add your first field below.
              </p>
            )}

            <ul className="space-y-2">
              {form.fields.map((field, index) => {
                const expanded = expandedKey === field._key;
                return (
                  <li
                    key={field._key}
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(index);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => onDragOverRow(e, index)}
                    onDragEnd={() => setDragIndex(null)}
                    className={`rounded-md border bg-background ${dragIndex === index ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-center gap-2 px-3 py-2">
                      <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden="true" />
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        onClick={() => setExpandedKey(expanded ? null : field._key)}
                        aria-expanded={expanded}
                      >
                        <span className="truncate text-sm font-medium">{field.label || "(no label)"}</span>
                        <Badge variant="secondary" className="shrink-0 text-[10px]">
                          {TYPE_LABELS[field.type] ?? field.type}
                        </Badge>
                        {field.required && (
                          <span className="shrink-0 text-xs text-muted-foreground">required</span>
                        )}
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={index === 0}
                        aria-label="Move field up"
                        onClick={() => moveField(index, -1)}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={index === form.fields.length - 1}
                        aria-label="Move field down"
                        onClick={() => moveField(index, 1)}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Delete field"
                        onClick={() => removeField(field._key)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <button
                        type="button"
                        className="text-muted-foreground"
                        aria-label={expanded ? "Collapse field settings" : "Expand field settings"}
                        onClick={() => setExpandedKey(expanded ? null : field._key)}
                      >
                        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </div>

                    {expanded && (
                      <div className="space-y-3 border-t bg-muted/30 px-3 py-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <ConfigInput
                            label={field.type === "consent" ? "Consent statement" : "Label"}
                            id={`${field._key}-label`}
                            value={field.label}
                            maxLength={200}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { label: e.target.value })}
                          />
                          <ConfigInput
                            label="Help text"
                            id={`${field._key}-help`}
                            value={field.helpText}
                            maxLength={500}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { helpText: e.target.value })}
                          />
                          {TEXTUAL_TYPES.has(field.type) && (
                            <>
                              <ConfigInput
                                label="Placeholder"
                                id={`${field._key}-placeholder`}
                                value={field.placeholder}
                                maxLength={200}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { placeholder: e.target.value })}
                              />
                              <ConfigInput
                                label="Default value"
                                id={`${field._key}-default`}
                                value={field.defaultValue}
                                maxLength={200}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { defaultValue: e.target.value })}
                              />
                            </>
                          )}
                          {field.type === "number" && (
                            <>
                              <ConfigInput
                                label="Minimum"
                                id={`${field._key}-min`}
                                type="number"
                                value={field.min ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { min: numberOrNull(e.target.value) })}
                              />
                              <ConfigInput
                                label="Maximum"
                                id={`${field._key}-max`}
                                type="number"
                                value={field.max ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { max: numberOrNull(e.target.value) })}
                              />
                            </>
                          )}
                          {(field.type === "text" || field.type === "textarea") && (
                            <ConfigInput
                              label="Max length"
                              id={`${field._key}-maxlength`}
                              type="number"
                              min={1}
                              max={5000}
                              value={field.maxLength ?? ""}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { maxLength: numberOrNull(e.target.value) })}
                            />
                          )}
                          {field.type === "file" && (
                            <ConfigInput
                              label="Max file size (MB)"
                              id={`${field._key}-maxsize`}
                              type="number"
                              min={1}
                              max={10}
                              value={field.maxSizeMb ?? ""}
                              placeholder="5"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { maxSizeMb: numberOrNull(e.target.value) })}
                            />
                          )}
                          <ConfigInput
                            label="CSS class (optional)"
                            id={`${field._key}-css`}
                            value={field.cssClass}
                            maxLength={100}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(field._key, { cssClass: e.target.value })}
                          />
                        </div>

                        {OPTION_TYPES.has(field.type) && (
                          <div className="space-y-1">
                            <Label htmlFor={`${field._key}-options`} className="text-xs">
                              Options (one per line)
                            </Label>
                            <Textarea
                              id={`${field._key}-options`}
                              rows={4}
                              value={(field.options ?? []).join("\n")}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setField(field._key, {
                                  options: e.target.value.split("\n").map((line) => line.trim()).filter(Boolean),
                                })
                              }
                            />
                          </div>
                        )}

                        {field.type === "file" && (
                          <fieldset>
                            <legend className="mb-1 text-xs font-medium">
                              Allowed file types (none ticked = all of these)
                            </legend>
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {FILE_EXTENSIONS.map((ext) => (
                                <label key={ext} className="flex items-center gap-1.5 text-sm">
                                  <Checkbox
                                    checked={(field.accept ?? []).includes(ext)}
                                    onChange={(e) =>
                                      setField(field._key, {
                                        accept: e.target.checked
                                          ? [...(field.accept ?? []), ext]
                                          : (field.accept ?? []).filter((item) => item !== ext),
                                      })
                                    }
                                  />
                                  .{ext}
                                </label>
                              ))}
                            </div>
                          </fieldset>
                        )}

                        {field.type !== "consent" && (
                          <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={field.required}
                              onChange={(e) => setField(field._key, { required: e.target.checked })}
                            />
                            Required field
                          </label>
                        )}
                        {field.type === "consent" && (
                          <p className="text-xs text-muted-foreground">
                            Consent boxes are always required — visitors must tick them to submit.
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Add a field</p>
              <div className="flex flex-wrap gap-2">
                {FIELD_TYPES.map(({ type, label }) => (
                  <Button key={type} variant="outline" size="sm" onClick={() => addField(type)}>
                    <Plus className="h-3.5 w-3.5" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">After submission</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <ConfigInput
              label="Submit button label"
              id="settings-submit"
              value={form.settings.submitLabel ?? ""}
              maxLength={50}
              placeholder="Send"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSetting({ submitLabel: e.target.value })}
            />
            <ConfigInput
              label="Redirect to URL (optional — overrides the message)"
              id="settings-redirect"
              value={form.settings.redirectUrl ?? ""}
              maxLength={300}
              placeholder="/thank-you/"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSetting({ redirectUrl: e.target.value || null })}
            />
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="settings-success" className="text-xs">Success message</Label>
              <Textarea
                id="settings-success"
                rows={2}
                maxLength={500}
                value={form.settings.successMessage ?? ""}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSetting({ successMessage: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Notifications & data retention</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <ConfigInput
              label="Send notification emails to (optional)"
              id="settings-notify"
              type="email"
              value={form.settings.notifyEmail ?? ""}
              maxLength={200}
              placeholder="Defaults to your contact email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSetting({ notifyEmail: e.target.value || null })}
            />
            <div className="space-y-1">
              <Label htmlFor="settings-retention" className="text-xs">
                Auto-delete submissions after (days)
              </Label>
              <Input
                id="settings-retention"
                type="number"
                min={1}
                max={3650}
                value={retentionValue}
                placeholder="Keep until deleted"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSetting({ retentionDays: numberOrNull(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">
                For GDPR: submissions (including uploaded files) older than this are deleted automatically.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
